import { FormControl, FormGroup } from '@angular/forms';
import { MetaTermSheetApi } from '@qtek/shared/models';

export type TermSheetFormControls = {
  closedDate: FormControl<string | null>;
  initialInterestRate: FormControl<number | null>;
  paymentFrequency: FormControl<string | null>;
  term: FormControl<number | null>;
  amortization: FormControl<number | null>;
  index: FormControl<string | null>;
  spread: FormControl<number | null>;
  rateResetFrequency: FormControl<string | null>;
  originalLoanAmount: FormControl<number | null>;
  principalBalance: FormControl<number | null>;
  purchasePrice: FormControl<number | null>;
  syndicationType: FormControl<string | null>;
  underwritingType: FormControl<string | null>;
  ltv: FormControl<number | null>;
  interestOnlyPeriod: FormControl<number | null>;
  agentBankHoldAmount: FormControl<number | null>;
  amountAvailableForPurchase: FormControl<number | null>;
  committedSubscriptionRate: FormControl<number | null>;
};

export type TermSheetFormValue = {
  [K in keyof TermSheetFormControls]: TermSheetFormControls[K] extends FormControl<
    infer V
  >
    ? V
    : never;
};

export const createTermSheetForm = (): FormGroup<TermSheetFormControls> =>
  new FormGroup<TermSheetFormControls>({
    closedDate: new FormControl<string | null>(null),
    initialInterestRate: new FormControl<number | null>(null),
    paymentFrequency: new FormControl<string | null>(null),
    term: new FormControl<number | null>(null),
    amortization: new FormControl<number | null>(null),
    index: new FormControl<string | null>(null),
    spread: new FormControl<number | null>(null),
    rateResetFrequency: new FormControl<string | null>(null),
    originalLoanAmount: new FormControl<number | null>(null),
    principalBalance: new FormControl<number | null>(null),
    purchasePrice: new FormControl<number | null>(null),
    syndicationType: new FormControl<string | null>(null),
    underwritingType: new FormControl<string | null>(null),
    ltv: new FormControl<number | null>(null),
    interestOnlyPeriod: new FormControl<number | null>(null),
    agentBankHoldAmount: new FormControl<number | null>(null),
    amountAvailableForPurchase: new FormControl<number | null>(null),
    committedSubscriptionRate: new FormControl<number | null>(null),
  });

type TermSheetPatchHelpers = {
  parseDateValue: (value: unknown) => string | null;
  parseNumber: (value: unknown) => number | null;
  parsePercent: (value: unknown) => number | null;
  toStringValue: (value: unknown) => string | null;
};

export const buildTermSheetPatch = (
  termSheet: MetaTermSheetApi,
  helpers: TermSheetPatchHelpers
): Partial<TermSheetFormValue> => ({
  closedDate: helpers.parseDateValue(termSheet.closeDate),
  initialInterestRate: helpers.parsePercent(termSheet.initialInterestRate),
  term: helpers.parseNumber(termSheet.term),
  amortization: helpers.parseNumber(termSheet.amortization),
  index: helpers.toStringValue(termSheet.index),
  spread: helpers.parsePercent(termSheet.spread),
  originalLoanAmount: helpers.parseNumber(termSheet.originalLoanAmount),
  ltv: helpers.parsePercent(termSheet.ltv),
  interestOnlyPeriod: helpers.parseNumber(termSheet.interestOnlyPeriod),
});

export const TERM_SHEET_FIELD_MAP: Record<string, string> = {
  closedDate: 'c.cDt',
  initialInterestRate: 'c.initInrRte',
  paymentFrequency: 'c.pmtFrq',
  term: 'c.trm',
  amortization: 'c.amtz',
  index: 'c.indx',
  spread: 'c.sprd',
  rateResetFrequency: 'c.rrf',
  originalLoanAmount: 'c.oAmt',
  principalBalance: 'a.pBlnc',
  purchasePrice: 'c.pp',
  syndicationType: 'c.sTp',
  underwritingType: 'c.uTp',
  ltv: 'l.ltv',
  interestOnlyPeriod: 'l.iop',
  agentBankHoldAmount: 'p.abha',
  amountAvailableForPurchase: 'p.parAvlSale',
  committedSubscriptionRate: 'p.parCmtSubRate',
};
